import { FaBoxes } from 'react-icons/fa';
import HomeIntroduction from 'components/home/HomeIntroduction';
import {guideImage} from 'utils/guideImage';
// src/pages/Home.js
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaPeopleCarry,
  FaShippingFast,
  FaPallet,
  FaCouch,
  FaTruckLoading,
} from 'react-icons/fa';
import HeroSection from 'components/home/HeroSection';
import BookingMethodSelector from 'components/home/BookingMethodSelector';
import ServiceTypeGrid from 'components/home/ServiceTypeGrid';

import 'styles/pages/home-improvements.css';
import 'styles/pages/home-redesign.css'; // Add the new redesign CSS

// Lazy load below-the-fold sections for faster initial render
const HomeReviews = lazy(() => import('components/reviews/HomeReviews'));
const GuidesCarousel = lazy(() => import('components/home/GuidesCarousel'));
const CurrentTasksSection = lazy(() => import('components/home/CurrentTasksSection'));

// Copied from HelpPage.js


const Home = () => {
  const navigate = useNavigate();
  const [homeContent, setHomeContent] = useState(null);
  useEffect(() => { let active = true; fetch('/api/content/homepage').then(r => r.ok ? r.json() : null).then(data => { if(active) setHomeContent(data); }).catch(() => {}); return () => { active = false; }; }, []);
  const [quoteCategory, setQuoteCategory] = useState(null); // Step 1: Mode Selection (null, 'instant', 'normal')
  const [presentation, setPresentation] = useState(null);
  const [loadingPublicTasks, setLoadingPublicTasks] = useState(true);
  const [guides, setGuides] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch('/api/content/type/GUIDE');
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled) return;
        
        if (Array.isArray(data)) {
          const dynamicGuides = data.map(item => {
            const image=guideImage(item);
            return {
              imgSrc: image.src,
              alt: image.alt,
              title: item.title,
              description: item.excerpt || item.metaDescription || "Læs guiden her",
              tags: item.tags || [],
              link: `/help/${item.slug}`,
              slug: item.slug
            };
          });
          
          setGuides(dynamicGuides);
        }
      } catch (e) {
        console.error("Failed to load dynamic guides:", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // SEO Injection
  useEffect(() => {
    // Title
    const originalTitle = document.title;
    document.title = "Flytte og Fragttilbud - Få billige tilbud på fragt og flytning";

    // Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    let originalDesc = metaDesc ? metaDesc.getAttribute('content') : '';
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', "Indhent uforpligtende tilbud på flytning og fragt fra verificerede vognmænd i Danmark, eller få en fast pris med det samme. Opret din opgave gratis.");

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://flytteogfragttilbud.dk/");

    // JSON-LD Structured Data
    const scriptId = "seo-json-ld";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.type = "application/ld+json";
      script.id = scriptId;
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "url": "https://flytteogfragttilbud.dk/",
        "name": "Foftilbud",
        "description": "Få billige tilbud på fragt og flytning",
        "publisher": {
          "@type": "Organization",
          "name": "Foftilbud",
          "logo": {
            "@type": "ImageObject",
            "url": "https://flytteogfragttilbud.dk/logo.png"
          }
        }
      };
      script.innerHTML = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      document.title = originalTitle;
      if (metaDesc && originalDesc) metaDesc.setAttribute('content', originalDesc);
      if (script) document.head.removeChild(script);
      if (canonical) document.head.removeChild(canonical);
    };
  }, []);

  const taskTypes = [
    { id: 'distribution', name: 'Distribution', icon: <FaBoxes />, description: 'Flere leveringer og opsamlinger', categories: ['normal'] },
    { id: 'moving', name: 'Flytning', icon: <FaPeopleCarry />, description: 'Flytning af møbler og ejendele', categories: ['instant', 'normal'] },
    { id: 'courier', name: 'Kurertransport', icon: <FaShippingFast />, description: 'Hurtig levering af pakker', categories: ['instant', 'normal'] },
    { id: 'package', name: 'Palle og pakker', icon: <FaPallet />, description: 'Transport af paller og større pakker', categories: ['normal'] },
    { id: 'furniture', name: 'Møbeltransport', icon: <FaCouch />, description: 'Specialtransport af møbler', categories: ['normal'] },
    { id: 'fullload', name: 'Fulloads', icon: <FaTruckLoading />, description: 'Komplet lastbil transport', categories: ['normal'] }
  ];

  const filteredTaskTypes = taskTypes.filter(type => type.categories.includes(quoteCategory));

  const startBooking = (type) => {
    const qs = `?type=${type}&category=${quoteCategory}`; // Pass the selected category
    navigate(`/create-task${qs}`);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingPublicTasks(true);
        const resp = await fetch('/api/site-settings/homepage', { cache: 'no-store' });
        if (!resp.ok) throw new Error('Failed');
        const data = await resp.json();
        if (cancelled) return;
        setPresentation(data);
      } catch {
        if (cancelled) return;
        setPresentation(null);
      } finally {
        if (!cancelled) setLoadingPublicTasks(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="page--home">
      <HeroSection content={homeContent} statistics={presentation?.presentationStatistics || presentation?.statistics} demoEnvironment={presentation?.publicationMode === 'staging_demo'} showCompletedTaskCount={presentation?.config.showCompletedTaskCount===true} showReviewCount={presentation?.config.showReviewCount===true} />

      <div className="home-quick-start-board">
        {quoteCategory === null ? (
          <BookingMethodSelector 
            currentMode={quoteCategory} 
            onModeSelect={setQuoteCategory} 
          />
        ) : (
          <ServiceTypeGrid 
            types={filteredTaskTypes} 
            currentMode={quoteCategory}
            onSelect={startBooking}
            onClearMode={() => setQuoteCategory(null)}
          />
        )}
      </div>

      <HomeIntroduction content={homeContent} />

      {/* Reviews Strip — Edge to edge scrolling ticker */}
      {presentation?.config.showReviews && <section className="reviews">
        <Suspense fallback={<div style={{ minHeight: '150px' }}></div>}>
          <HomeReviews max={50} initialReviews={presentation.reviews} demoEnvironment={presentation.publicationMode === 'staging_demo'} curated={presentation.config.reviewMode === 'selected'} />
        </Suspense>
      </section>}

      <Suspense fallback={<div style={{ minHeight: '200px' }}></div>}>
        {loadingPublicTasks ? <p role="status">Henter offentlige opgaver...</p> : presentation?.config.showTasks ? <CurrentTasksSection tasks={presentation.tasks} demoEnvironment={presentation.publicationMode === 'staging_demo'} /> : !presentation ? <p role="alert">Offentlige opgaver og anmeldelser kunne ikke hentes. Prøv at genindlæse siden.</p> : null}
      </Suspense>

      <Suspense fallback={<div style={{ minHeight: '300px' }}></div>}>
        <GuidesCarousel guides={guides} />
      </Suspense>

      {/* SEO Internal Linking Footer (Minimal styling to fit UX) */}
      {/* <section className="seo-internal-links" style={{ padding: '2rem 1rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#1e293b' }}>Læs mere om vores services</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center', maxWidth: '800px', margin: '0 auto' }}>
          <a href="/help/flytning" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>Flytteguides</a>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <a href="/help/emballering" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>Pakke & Emballage</a>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <a href="/help/faq" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>Ofte Stillede Spørgsmål (FAQ)</a>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <a href="/about" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>Om os</a>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <a href="/contact" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>Kontakt</a>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <a href="/terms" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>Vilkår & Betingelser</a>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <a href="/privacy-policy" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>Privatlivspolitik</a>
        </div>
      </section> */}
    </div>
  );
};

export default Home;
